import React, { useMemo } from "react";

import { Dashboard } from "api/model/dashboard/Dashboard";
import { Button } from "primereact/button";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { Menu } from "primereact/menu";
import { SelectItem } from "primereact/selectitem";
import { useNavigate } from "react-router-dom";
import { I, T } from "services/lang/LanguageService";
import "../dashboard-styles/DashboardMain.scss";
import { getMenuItems } from "../utils";

interface DashboardSubTabsDropdownMenuProps {
  isMobile: boolean;
  menuRef: React.RefObject<Menu | null>;
  tabId: number;
  dashboard: Dashboard;
}

enum ArrowDirection {
  UP = "up",
  DOWN = "down",
}

const UNGROUPED = "ungrouped";

const DashboardSubTabsDropdownMenu = ({
  isMobile,
  menuRef,
  tabId,
  dashboard,
}: DashboardSubTabsDropdownMenuProps) => {
  const navigate = useNavigate();

  const selectedTabGroupUUID = useMemo(() => {
    return dashboard?.dashboardTabsJs?.[tabId]?.tabGroupId ?? UNGROUPED;
  }, [dashboard?.dashboardTabsJs, tabId]);

  const selectedTabUUID = useMemo(() => {
    return dashboard?.dashboardTabsJs?.[tabId]?.uuid;
  }, [dashboard?.dashboardTabsJs, tabId]);

  const hasUngroupedTabs = useMemo(() => {
    return dashboard?.dashboardTabsJs?.some(tab => !tab.tabGroupId);
  }, [dashboard?.dashboardTabsJs]);

  const handleTabChange = (
    tabUUID: string,
    e?: DropdownChangeEvent | { originalEvent?: Event }
  ) => {
    const selectedIndex = dashboard.dashboardTabsJs.findIndex(tab => tab.uuid === tabUUID);

    const isCtrlOrMetaPressed =
      e?.originalEvent &&
      ("ctrlKey" in e.originalEvent || "metaKey" in e.originalEvent) &&
      ((e.originalEvent as MouseEvent).ctrlKey || (e.originalEvent as MouseEvent).metaKey);

    if (!isMobile && isCtrlOrMetaPressed) {
      const newTabUrl = `${window.location.pathname}/#/dashboard/${dashboard.id}/tab/${selectedIndex}`;
      window.open(newTabUrl, "_blank");
    } else {
      const url = `/dashboard/${dashboard.id}/tab/${selectedIndex}`;
      navigate(url);
    }
  };

  const changeTabWithArrow = (
    direction: ArrowDirection,
    items: SelectItem[],
    selectedId: string,
    isGroup = false
  ) => {
    const currentIndex = items.findIndex(node => node.value === selectedId);
    if (currentIndex === -1) return;

    const newIndex = direction === ArrowDirection.DOWN ? currentIndex + 1 : currentIndex - 1;

    if (newIndex < 0 || newIndex >= items.length) return;

    const tabUUID = dashboard.dashboardTabsJs.find(tab => {
      if (items[newIndex].value === UNGROUPED) return !tab.tabGroupId;
      return isGroup
        ? tab.tabGroupId === items[newIndex].value
        : tab.uuid === items[newIndex].value;
    }
    )?.uuid;

    if (!tabUUID) return;
    handleTabChange(tabUUID);
  };

  const tabGroups = useMemo(() => {
    const groupIds = new Set(dashboard.dashboardTabsJs.map(tab => tab.tabGroupId));
    const tabGroupList = [...(dashboard.tabGroups ?? [])].reduce(
      (acc, group) =>
        groupIds.has(group.uuid)
          ? [
              ...acc,
              {
                value: group.uuid,
                label: I(group.title),
              },
            ]
          : acc,
      [] as SelectItem[]
    );

    return hasUngroupedTabs
      ? [{ value: UNGROUPED, label: T("label.unassigned") }, ...tabGroupList]
      : tabGroupList;
  }, [dashboard.dashboardTabsJs, dashboard.tabGroups, hasUngroupedTabs]);

  const tabsInGroup = useMemo(() => {
    return [...(dashboard.dashboardTabsJs ?? [])].reduce((acc, tab) => {
      if (selectedTabGroupUUID === UNGROUPED) {
        return !tab.tabGroupId ? [...acc, { value: tab.uuid, label: I(tab.title) }] : acc;
      }
      return tab.tabGroupId === selectedTabGroupUUID
        ? [
            ...acc,
            {
              value: tab.uuid,
              label: I(tab.title),
            },
          ]
        : acc;
    }, [] as SelectItem[]);
  }, [dashboard.dashboardTabsJs, selectedTabGroupUUID]);

  const handleTabGroupChange = (e: DropdownChangeEvent) => {
    const selectedValue = e.value as string;
    const tabs = dashboard.dashboardTabsJs.filter(
      tab => {
        if (selectedValue === UNGROUPED) return !tab.tabGroupId;
        return tab.tabGroupId === selectedValue || tab.uuid === selectedValue;
      }
    );

    if (tabs.length === 0) return;
    handleTabChange(tabs[0].uuid, e);
  };

  return (
    <div>
      {isMobile ? (
        <Menu
          ref={menuRef}
          model={getMenuItems(tabGroups, handleTabChange)}
          popup
          className="w-12 menu-list"
        />
      ) : (
        <div className="flex flex-row gap-2 items-center tabs-dropdown">
          <Dropdown
            value={selectedTabGroupUUID}
            options={tabGroups}
            onChange={handleTabGroupChange}
            className="w-21rem"
            filter
            showFilterClear
            placeholder={T("action.select-group")}
          />
          {dashboard.showTabNavigation && (
            <>
              <Button
                icon="pi pi-arrow-left"
                className="p-button-text"
                disabled={tabGroups.findIndex(node => node.value === selectedTabGroupUUID) <= 0}
                tooltip={T("action.select-previous-tab-group")}
                tooltipOptions={{ position: "bottom" }}
                onClick={() =>
                  changeTabWithArrow(ArrowDirection.UP, tabGroups, selectedTabGroupUUID!, true)
                }
              />
              <Button
                icon="pi pi-arrow-right"
                className="p-button-text"
                disabled={
                  tabGroups.findIndex(node => node.value === selectedTabGroupUUID) ===
                  tabGroups.length - 1
                }
                tooltip={T("action.select-next-tab-group")}
                tooltipOptions={{ position: "bottom" }}
                onClick={() =>
                  changeTabWithArrow(ArrowDirection.DOWN, tabGroups, selectedTabGroupUUID!, true)
                }
              />
            </>
          )}
          <Dropdown
            value={selectedTabUUID}
            options={tabsInGroup}
            onChange={e => handleTabChange(e.value, e)}
            className="w-21rem"
            filter
            placeholder={T("action.select-group")}
          />
          {dashboard.showTabNavigation && tabsInGroup.length > 1 && (
            <>
              <Button
                icon="pi pi-arrow-left"
                className="p-button-text"
                disabled={tabsInGroup.findIndex(node => node.value === selectedTabUUID) <= 0}
                tooltip={T("action.select-previous-tab")}
                tooltipOptions={{ position: "bottom" }}
                onClick={() => changeTabWithArrow(ArrowDirection.UP, tabsInGroup, selectedTabUUID!)}
              />
              <Button
                icon="pi pi-arrow-right"
                className="p-button-text"
                disabled={
                  tabsInGroup.findIndex(node => node.value === selectedTabUUID) ===
                  tabsInGroup.length - 1
                }
                tooltip={T("action.select-next-tab")}
                tooltipOptions={{ position: "bottom" }}
                onClick={() =>
                  changeTabWithArrow(ArrowDirection.DOWN, tabsInGroup, selectedTabUUID!)
                }
              />
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default DashboardSubTabsDropdownMenu;
